import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchOrganisations, createOrganisation, setFilters, clearFilters } from '../../store/organisationSlice';
import { OrgPlan } from '../../types';

const UserMemberships = React.lazy(() => import('./UserMemberships'));
const OrganisationDetail = React.lazy(() => import('./OrganisationDetail'));

const PLAN_COLORS: Record<OrgPlan, string> = { enterprise: 'bg-yellow-100 text-yellow-700' };
const PLAN_LABELS: Record<OrgPlan, string> = { enterprise: 'Enterprise' };

type OrgTab = 'organisations' | 'assign';
const TABS: { key: OrgTab; label: string }[] = [
  { key: 'organisations', label: 'Organisations' },
  { key: 'assign', label: 'Assign User' },
];

interface AdminUserForm { name: string; email: string; password: string; confirmPassword: string; phone: string; }
const defaultAdminUser: AdminUserForm = { name: '', email: '', password: '', confirmPassword: '', phone: '' };

const OrganisationManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as OrgTab | null;
  const [activeTab, setActiveTab] = useState<OrgTab>(tabParam && TABS.some(t => t.key === tabParam) ? tabParam : 'organisations');

  const { organisations, loading, error, filters, pagination } = useAppSelector(s => s.organisations);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(searchParams.get('orgId') || null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({ name: '', slug: '', description: '', contactEmail: '', timezone: 'UTC', plan: 'enterprise' as OrgPlan });
  const [adminUser, setAdminUser] = useState<AdminUserForm>(defaultAdminUser);
  const [includeAdmin, setIncludeAdmin] = useState(true);

  useEffect(() => { dispatch(fetchOrganisations(filters)); }, [dispatch, filters]);

  const openOrgDetail = (orgId: string) => {
    setSelectedOrgId(orgId);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('orgId', orgId); return n; }, { replace: true });
  };

  const closeOrgDetail = () => {
    setSelectedOrgId(null);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('orgId'); return n; }, { replace: true });
    dispatch(fetchOrganisations(filters));
  };

  const handleTabChange = (tab: OrgTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', description: '', contactEmail: '', timezone: 'UTC', plan: 'enterprise' });
    setAdminUser(defaultAdminUser); setIncludeAdmin(true); setCreateError(''); setShowPassword(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault(); setCreateError('');
    if (includeAdmin) {
      if (!adminUser.name.trim() || !adminUser.email.trim() || !adminUser.password) { setCreateError('Admin user requires name, email, and password'); return; }
      if (adminUser.password !== adminUser.confirmPassword) { setCreateError('Passwords do not match'); return; }
      if (adminUser.password.length < 8) { setCreateError('Password must be at least 8 characters'); return; }
    }
    setCreateLoading(true);
    try {
      const payload: any = { ...formData };
      if (includeAdmin) payload.adminUser = { name: adminUser.name.trim(), email: adminUser.email.trim(), password: adminUser.password, phone: adminUser.phone.trim() || undefined };
      const result = await dispatch(createOrganisation(payload)).unwrap();
      setShowCreateModal(false); resetForm();
      const adminCreated = (result as any).adminUser;
      setSuccessMsg(adminCreated ? `Organisation created. Admin account for ${adminCreated.email} is ready.` : 'Organisation created successfully');
      setTimeout(() => setSuccessMsg(''), 5000);
      dispatch(fetchOrganisations(filters));
    } catch (err: any) { setCreateError(typeof err === 'string' ? err : 'Failed to create organisation'); }
    setCreateLoading(false);
  };

  const handleFilterChange = (key: string, value: string) => {
    const update: any = {};
    if (key === 'isActive') update.isActive = value === '' ? undefined : value === 'true';
    else update[key] = value || undefined;
    dispatch(setFilters(update));
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const activeCount = organisations.filter(o => o.isActive).length;
  const suspendedCount = organisations.filter(o => !o.isActive).length;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Organisations</h1>
        <p className="text-sm text-gray-400 mt-0.5">Manage tenants, settings and user assignments</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map(t => (
            <button key={t.key} onClick={() => handleTabChange(t.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'organisations' && (
        <div>
          {successMsg && <div className="mx-6 mt-3 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">{successMsg}</div>}
          {error && <div className="mx-6 mt-3 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>}

          {/* Summary + filter bar */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-gray-900">{pagination.total}</span>
              <span className="text-[10px] text-gray-400">Organisations</span>
            </div>
            {activeCount > 0 && (
              <button onClick={() => handleFilterChange('isActive', filters.isActive === true ? '' : 'true')}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full transition-colors ${filters.isActive === true ? 'bg-gray-100 ring-1 ring-offset-1 ring-gray-300' : 'hover:bg-gray-50'}`}>
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className={`text-sm font-semibold ${filters.isActive === true ? 'text-green-700' : 'text-gray-500'}`}>{activeCount}</span>
                <span className="text-[10px] text-gray-400">Active</span>
              </button>
            )}
            {suspendedCount > 0 && (
              <button onClick={() => handleFilterChange('isActive', filters.isActive === false ? '' : 'false')}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full transition-colors ${filters.isActive === false ? 'bg-gray-100 ring-1 ring-offset-1 ring-gray-300' : 'hover:bg-gray-50'}`}>
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className={`text-sm font-semibold ${filters.isActive === false ? 'text-red-600' : 'text-gray-500'}`}>{suspendedCount}</span>
                <span className="text-[10px] text-gray-400">Suspended</span>
              </button>
            )}
            {(filters.isActive !== undefined) && (
              <>
                <div className="w-px h-4 bg-gray-200" />
                <button onClick={() => dispatch(clearFilters())} className="text-[10px] text-gray-400 hover:text-gray-600">Clear filter</button>
              </>
            )}
            <button onClick={() => setShowCreateModal(true)} className="ml-auto text-xs text-gray-400 hover:text-blue-600 transition-colors">+ New Organisation</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : organisations.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-sm font-medium text-gray-700">No organisations</p>
              <p className="text-xs text-gray-400 mt-1">Create the first one to get started</p>
            </div>
          ) : (
            <div>
              {/* Table header */}
              <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
                <div className="col-span-3">Organisation</div>
                <div className="col-span-2">Slug</div>
                <div className="col-span-1">Plan</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-1 text-center">Users</div>
                <div className="col-span-1 text-center">Projects</div>
                <div className="col-span-1 text-center">Admins</div>
                <div className="col-span-2 text-right">Created</div>
              </div>

              {/* Table rows */}
              {organisations.map(org => (
                <div key={org._id} onClick={() => openOrgDetail(org._id)}
                  className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
                  <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                    {org.logoUrl ? (
                      <img src={org.logoUrl} alt={org.name} className="w-7 h-7 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                        <span className="text-blue-700 font-bold text-[10px]">{org.name.slice(0, 2).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{org.name}</div>
                      {org.description && <div className="text-xs text-gray-400 truncate">{org.description}</div>}
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center text-xs text-gray-500 font-mono truncate">{org.slug}</div>
                  <div className="col-span-1 flex items-center">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[org.plan] || 'bg-gray-100 text-gray-600'}`}>{PLAN_LABELS[org.plan] || org.plan}</span>
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${org.isActive ? 'bg-green-500' : 'bg-red-400'}`} />
                      <span className="text-xs text-gray-500">{org.isActive ? 'Active' : 'Suspended'}</span>
                    </div>
                  </div>
                  <div className="col-span-1 flex items-center justify-center text-xs text-gray-700 tabular-nums">{org.limits.maxUsers === -1 ? '∞' : org.limits.maxUsers}</div>
                  <div className="col-span-1 flex items-center justify-center text-xs text-gray-700 tabular-nums">{org.limits.maxProjects === -1 ? '∞' : org.limits.maxProjects}</div>
                  <div className="col-span-1 flex items-center justify-center text-xs text-blue-600 font-medium tabular-nums">{org.adminUserIds.length}</div>
                  <div className="col-span-2 flex items-center justify-end text-xs text-gray-400">{fmtDate(org.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'assign' && (
        <React.Suspense fallback={<div className="flex items-center justify-center py-20"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>}>
          <UserMemberships />
        </React.Suspense>
      )}

      {/* ── Organisation Detail Modal ─────────────────────────────────── */}
      {selectedOrgId && (
        <React.Suspense fallback={
          <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30" />
            <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex items-center justify-center border border-gray-200">
              <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          </div>
        }>
          <OrganisationDetail orgId={selectedOrgId} onClose={closeOrgDetail} isModal />
        </React.Suspense>
      )}

      {/* ── Create Organisation Modal ─────────────────────────────────── */}
      {showCreateModal && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setShowCreateModal(false); resetForm(); }}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden border border-gray-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
              <span className="text-sm font-semibold text-gray-900">New Organisation</span>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {createError && <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{createError}</div>}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Name <span className="text-red-500">*</span></label>
                  <input required value={formData.name} onChange={e => {
                    const name = e.target.value;
                    setFormData({ ...formData, name, slug: name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-') });
                  }} placeholder="Acme Corp" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Slug</label>
                  <input value={formData.slug} onChange={e => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                    placeholder="acme-corp" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 font-mono" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Description</label>
                <textarea rows={2} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What does this organisation do?" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Contact Email</label>
                  <input type="email" value={formData.contactEmail} onChange={e => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="hello@acme.com" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Timezone</label>
                  <input value={formData.timezone} onChange={e => setFormData({ ...formData, timezone: e.target.value })}
                    placeholder="Africa/Nairobi" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                </div>
              </div>

              {/* Admin user section */}
              <div className="border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">Organisation Admin</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <button type="button" onClick={() => setIncludeAdmin(!includeAdmin)}
                      className={`relative inline-flex h-4 w-8 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${includeAdmin ? 'bg-blue-600' : 'bg-gray-200'}`}>
                      <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition ${includeAdmin ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <span className="text-xs text-gray-500">Create admin</span>
                  </label>
                </div>

                {includeAdmin ? (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-3 border border-gray-100">
                    <p className="text-[10px] text-gray-400">Admin will be prompted to change password on first login.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Full Name <span className="text-red-500">*</span></label>
                        <input value={adminUser.name} onChange={e => setAdminUser({ ...adminUser, name: e.target.value })}
                          placeholder="Jane Doe" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Phone</label>
                        <input type="tel" value={adminUser.phone} onChange={e => setAdminUser({ ...adminUser, phone: e.target.value })}
                          placeholder="+254 700 000000" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Email <span className="text-red-500">*</span></label>
                      <input type="email" value={adminUser.email} onChange={e => setAdminUser({ ...adminUser, email: e.target.value })}
                        placeholder="jane@acme.com" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Password <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <input type={showPassword ? 'text' : 'password'} value={adminUser.password}
                            onChange={e => setAdminUser({ ...adminUser, password: e.target.value })} placeholder="Min. 8 chars"
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-1 focus:ring-gray-300" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d={showPassword ? "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" : "M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"} />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Confirm <span className="text-red-500">*</span></label>
                        <input type={showPassword ? 'text' : 'password'} value={adminUser.confirmPassword}
                          onChange={e => setAdminUser({ ...adminUser, confirmPassword: e.target.value })} placeholder="Repeat"
                          className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 ${adminUser.confirmPassword && adminUser.password !== adminUser.confirmPassword ? 'border-red-400' : 'border-gray-200'}`} />
                        {adminUser.confirmPassword && adminUser.password !== adminUser.confirmPassword && (
                          <p className="text-[10px] text-red-500 mt-0.5">Passwords do not match</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No admin account — you can add admins later.</p>
                )}
              </div>
            </form>

            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0">
              <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-xs text-gray-500 px-4 py-2 hover:bg-gray-50 rounded-lg">Cancel</button>
              <button onClick={handleCreate as any} disabled={createLoading}
                className="text-xs bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-30 transition-colors">
                {createLoading ? 'Creating...' : 'Create Organisation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganisationManagement;
